/** @jsx jsx */

import { Fragment as _, useState, useRef, useEffect, useCallback } from 'react';
import { css, jsx } from '@emotion/core';
import getBlockById from 'services/apiHelpers/getBlockById';
import { useCachedResource } from 'services/useCachedResource';
import { CHAMELEON_CACHE_v1 } from 'services/imageCache';
import { Orbs, getAnimatedOrbsDefaultValues } from './Orbs';
import { Graphical_BKD_Hills } from 'components/Editor/OnboardingWizard/Graphical_BKD_Hills';
import { Graphical_BKD_Squiggle } from 'components/Editor/OnboardingWizard/Graphical_BKD_Squiggle';
import { Graphical_BKD_Clouds } from 'components/Editor/OnboardingWizard/Graphical_BKD_Clouds';
import {
  Clouds,
  cloudsSVGToString,
} from 'components/Editor/OnboardingWizard/Clouds';
import {
  Squiggle,
  squiggleSVGToString,
} from 'components/Editor/OnboardingWizard/Squiggle';
import {
  Waves as Hills,
  wavesSVGToString as hillsSVGToString,
} from 'components/Editor/OnboardingWizard/Waves';
import {
  Bubbles,
  bubblesSVGToString,
} from 'components/Editor/OnboardingWizard/Bubbles';
import { Arc, arcSVGToString } from 'components/Editor/OnboardingWizard/Arc';
import { Flow, flowSVGToString } from 'components/Editor/OnboardingWizard/Flow';
import { motion } from 'framer-motion';
import { svgStringEncode } from 'services/svgStringEncode';

const chameleonBackgroundImgs = {
  chameleonBackgroundImage_Clouds: Clouds || Graphical_BKD_Clouds,
  chameleonBackgroundImage_Squiggle: Squiggle || Graphical_BKD_Squiggle,
  chameleonBackgroundImage_Hills: Hills || Graphical_BKD_Hills,
  chameleonBackgroundImage_Bubbles: Bubbles,
  chameleonBackgroundImage_Arc: Arc,
  chameleonBackgroundImage_Flow: Flow,
};

const chameleonBackgroundSVGStrings = {
  Clouds: cloudsSVGToString,
  Squiggle: squiggleSVGToString,
  Hills: hillsSVGToString,
  Bubbles: bubblesSVGToString,
  Arc: arcSVGToString,
  Flow: flowSVGToString,
};

const Background = ({
  id,
  background,
  backgroundImageOpacity,
  backgroundBlur = 0,
}) => {
  let cachedBackgroundImage = useCachedResource(CHAMELEON_CACHE_v1, background);

  return (
    <div
      id={`topic-background-${id}`}
      style={{
        position: 'fixed',
        left: 0,
        top: 0,
        width: '100vw',
        height: '100vh',
        ...(background && {
          background: `url('${cachedBackgroundImage ||
            background}') center / cover no-repeat`,
        }),

        opacity: backgroundImageOpacity / 100,
        filter: `blur(${backgroundBlur}px)`,
        // scale the thing up to not have blurry edges
        ...(backgroundBlur > 0 && {
          transformOrigin: 'center',
          transform: 'scale(1.1)',
        }),
        pointerEvents: 'none',
      }}
    />
  );
};

const BackgroundWithParallax = props => {
  const {
    interactions,
    background,
    backgroundImageOpacity,
    backgroundBlur = 0,
    parallaxValue = 10,
    width,
    height,
  } = props;

  const cachedBackgroundImage = useCachedResource(
    CHAMELEON_CACHE_v1,
    background
  );

  const [
    refreshParallaxScrollOnTopicChange,
    setRefreshParallaxScrollOnTopicChange,
  ] = useState<boolean>(false);

  const img = useRef<HTMLImageElement | null>(null);
  const setImageRef = useCallback(node => {
    img.current = node;
  }, []);

  const minHeight = `${100 + parallaxValue}vh`;

  useEffect(() => {
    if (!img.current) {
      return;
    }

    const parallaxThrottler = () => {
      // we can implement whatever more specific throttling approach...
      requestAnimationFrame(parallax);
    };

    const parallax = () => {
      if (!img.current) {
        return;
      }

      const { innerHeight, scrollY } = window;
      const imgTravel = img.current.offsetHeight - innerHeight;
      const scrollTravel = innerHeight * (interactions.length - 1);
      const scrollUnit = imgTravel / scrollTravel;
      const scrollValue = scrollY * scrollUnit;

      if (scrollValue > imgTravel) {
        return;
      }

      //
      img.current.style.transform = `translateY(-${scrollValue}px)`;
    };
    //

    window.addEventListener('scroll', parallaxThrottler);
    window.addEventListener('resize', parallaxThrottler);
    parallax();

    return () => {
      window.removeEventListener('scroll', parallaxThrottler);
      window.removeEventListener('resize', parallaxThrottler);
    };
  }, [setImageRef, refreshParallaxScrollOnTopicChange]);

  useEffect(() => {
    if (!img.current) {
      return;
    }

    let tick;

    if (document.body.scrollHeight > window.innerHeight) {
      const imgHeightOnLoad = img.current.offsetHeight;

      let checkLimit = 0;

      tick = setInterval(() => {
        if (!img.current) {
          return;
        }

        if (img.current.offsetHeight !== imgHeightOnLoad) {
          clearInterval(tick);
          setRefreshParallaxScrollOnTopicChange(true);
        }

        //
        if (checkLimit >= 300) {
          clearInterval(tick);
        }
        checkLimit += 1;
        //
      }, 1);
    }

    return () => {
      clearInterval(tick);
    };
  }, [setImageRef, setRefreshParallaxScrollOnTopicChange]);

  return (
    <_>
      <div
        style={{
          position: 'fixed',
          left: 0,
          top: 0,
          width: '100vw',
          height: '100vh',
          display: 'flex',
          justifyContent: 'center',
          pointerEvents: 'none',
        }}
      >
        <img
          ref={setImageRef}
          src={cachedBackgroundImage || background}
          style={{
            all: 'initial',
            position: 'absolute',
            objectFit: 'cover',
            minWidth: '100vw',
            minHeight,
            opacity: backgroundImageOpacity / 100,
            filter: `blur(${backgroundBlur}px)`,
          }}
        />
      </div>
    </_>
  );
};

const GradientBackground = ({ backgroundGradientData }) => {
  const {
    backgroundPrimaryColor,
    backgroundSecondaryColor,
    backgroundGradientAngle = 0,
  } = backgroundGradientData || {};

  return (
    <div
      css={css({
        position: 'fixed',
        width: '100vw',
        height: '100vh',
        background: `linear-gradient(${backgroundGradientAngle}deg, ${backgroundSecondaryColor}, ${backgroundPrimaryColor})`,
      })}
    />
  );
};

const defaultChameleonBackgroundColor = '#4EA3F7';

const TopicContainer = props => {
  const { state, theme, topic, children } = props as any;

  const block: any = getBlockById(topic.id);

  let {
    src,
    imageId,
    width,
    height,
    backgroundImage,
    chameleonBackground,
    backgroundImageOpacity = 100,
    backgroundBlur = 0,
    parallax = false,
    parallaxValue = 10,
    backgroundGradient,
    backgroundSecondaryColor,
    backgroundGradientAngle = 0,
    backgroundGradientFullHeight = false,
    backgroundStyle,
  }: any = block.props;

  const chameleonBackgroundColor =
    block?.props?.chameleonBackgroundColor || defaultChameleonBackgroundColor;

  const chameleonBackgroundSVGEncoded = chameleonBackgroundSVGStrings[
    chameleonBackground
  ]
    ? svgStringEncode(
        chameleonBackgroundSVGStrings[chameleonBackground](
          chameleonBackgroundColor
        )
      )
    : '';

  const {
    primaryOrbColor,
    secondaryOrbColor,
    tertiaryOrbColor,
    orbOpacity,
    orbBlurStrength,
    orbSpeed,
  } = getAnimatedOrbsDefaultValues(block.props);

  const main = useRef<HTMLDivElement>(null);

  // dirty gotta try and cover for legacy backgroundImage
  if (src) {
    backgroundImage = src;
  }

  let backgroundColor =
    block.props.backgroundColor || theme.backgroundColor || '';

  if (backgroundGradient && backgroundGradientFullHeight) {
    backgroundColor = `linear-gradient(${backgroundGradientAngle}deg, ${backgroundSecondaryColor}, ${block.props.backgroundColor})`;
  }

  // this conditional bit evaluates if there was an image in the legacy format. but backgroundStyle is undefined
  const _backgroundImage_ =
    backgroundStyle === undefined && backgroundImage
      ? backgroundImage
      : backgroundStyle === 'backgroundImage'
      ? backgroundImage
      : undefined;

  //
  const backgroundHeightCalculated = 100 * children?.props?.interactions.length;
  // progressive vHeights. . .
  const backgroundSize = `
  background-size: 100vw ${backgroundHeightCalculated}vh;
  background-size: 100vw ${backgroundHeightCalculated}dvh;
`;
  //

  return (
    //@ts-ignore
    <motion.div
      id="main"
      ref={main}
      initial={{ opacity: 0 }}
      transition={{
        duration: 0.5,
        ease: 'easeOut',
      }}
      animate={{ opacity: 1 }}
      //

      tabIndex={-1}
      css={css`
        position: relative;
        background: ${backgroundColor};
        ${backgroundGradient && backgroundGradientFullHeight && backgroundSize}
      `}
    >
      {backgroundGradient && !backgroundGradientFullHeight && (
        <GradientBackground
          backgroundGradientData={{
            backgroundGradientAngle,
            backgroundPrimaryColor: backgroundColor,
            backgroundSecondaryColor,
          }}
        />
      )}

      {window.dev() || !parallax ? (
        <Background
          id={topic.id}
          background={_backgroundImage_}
          backgroundImageOpacity={backgroundImageOpacity}
          backgroundBlur={backgroundBlur}
        />
      ) : (
        <BackgroundWithParallax
          interactions={children.props.interactions}
          background={_backgroundImage_}
          backgroundImageOpacity={backgroundImageOpacity}
          backgroundBlur={backgroundBlur}
          parallaxValue={parallaxValue}
          width={width}
          height={height}
        />
      )}

      {backgroundStyle === 'animatedOrbs' && (
        <Orbs
          {...{
            orbsFor: 'topic',
            primaryOrbColor,
            secondaryOrbColor,
            tertiaryOrbColor,
            orbOpacity,
            orbBlurStrength,
            orbSpeed,
          }}
        />
      )}

      {backgroundStyle &&
        chameleonBackground &&
        backgroundStyle === chameleonBackground && (
          <img
            id={`chameleon-background-${topic.id}`}
            src={chameleonBackgroundSVGEncoded}
            alt="Encoded SVG"
            css={css({
              position: 'fixed',
              left: 0,
              top: 0,

              width: '100vw',
              height: '100%',
              objectFit: 'cover',
            })}
          />

          // for the old times. . . {/* <ChameleonBackground color={chameleonBackgroundColor} /> */}
        )}

      {children}
    </motion.div>
  );
};

export default TopicContainer;
